// ABOUTME: Types of the job module: the v2 job REST shapes (jobs, attempts, logs, types, schedules)
// ABOUTME: and the per-type `result` payloads the overview and drill-down tables render.

export type JobState =
    | 'queued'
    | 'running'
    | 'retrying'
    | 'succeeded'
    | 'failed'
    | 'cancelled'
    | 'skipped';

export type AttemptState =
    | 'pending'
    | 'running'
    | 'succeeded'
    | 'failed'
    | 'timedOut'
    | 'cancelled';

export type AttemptReason = 'oom' | 'lost' | 'interrupted' | 'unschedulable' | 'image' | 'exit';

export type JobTrigger = 'manual' | 'schedule' | 'retry' | 'system' | 'api';

export type JobExecutor = 'k8s' | 'inline';

export type JobSkipReason = 'missed' | 'concurrent';

export type LogLevel = 'info' | 'warn' | 'error';

/** Kubernetes-style resource quantities, e.g. { requests: { cpu: '500m', memory: '1Gi' } }. */
export type JobResources = {
    requests?: Record<string, string>;
    limits?: Record<string, string>;
};

/** The states a job can still move out of on its own. */
export const NON_TERMINAL_JOB_STATES: JobState[] = ['queued', 'running', 'retrying'];

export const TERMINAL_JOB_STATES: JobState[] = ['succeeded', 'failed', 'cancelled', 'skipped'];

/** The attempt states a retry can start from. */
export const RETRYABLE_ATTEMPT_STATES: AttemptState[] = ['failed', 'timedOut', 'cancelled'];

export const LIVE_ATTEMPT_STATES: AttemptState[] = ['pending', 'running'];

export type JobAttempt = {
    n: number;
    state: AttemptState;
    executor: JobExecutor;
    reason: AttemptReason | null;
    exitCode: number | null;
    progress: number;
    progressMessage: string | null;
    notBefore: string | null;
    startedAt: string | null;
    endedAt: string | null;
    heartbeatAt: string | null;
    /** The resources the attempt actually ran with. */
    resources: JobResources | null;
    /** What the job reported through updateData (the old descriptor's `data`). */
    result: unknown;
    filename: string | null;
};

/** A job as GET /api/job/jobs and GET /api/job/jobs/:id return it. */
export type Jobs = {
    id: string;
    type: string;
    state: JobState;
    skipReason: JobSkipReason | null;
    trigger: JobTrigger;
    executor: JobExecutor;
    company: string | null;
    args: Record<string, unknown>;
    createdBy: { username: string | null };
    username: string | null;
    retriedBy: string | null;
    scheduleId: string | null;
    scheduledFor: string | null;
    resources: JobResources | null;
    /** Launched by a superadmin without the type's maxConcurrent check. */
    skipConcurrencyCheck: boolean;
    createdAt: string | null;
    startedAt: string | null;
    endedAt: string | null;
    cancelRequestedAt: string | null;
    currentAttempt: number;
    attemptsCount: number;
    /** Progress of the current attempt. */
    progress: number;
    progressMessage: string | null;
    /** Result of the current attempt. */
    result: unknown;
    /** File of the latest attempt that produced one. */
    filename: string | null;
    /** Only on GET /api/job/jobs/:id. */
    attempts?: JobAttempt[];
};

export type JobListResponse = { jobs: Jobs[]; nextCursor: string | null };

export type JobLogEntry = {
    seq: number;
    ts: string;
    level: LogLevel;
    message: string;
    data: unknown;
};

export type JobLogPage = { entries: JobLogEntry[]; lastSeq: number; hasMore: boolean };

/** A JSON schema node, as TypeBox serializes it (only the keywords the forms read). */
export type JobArgsSchema = {
    type?: string | string[];
    title?: string;
    description?: string;
    properties?: Record<string, JobArgsSchema>;
    required?: string[];
    items?: JobArgsSchema;
    anyOf?: JobArgsSchema[];
    enum?: unknown[];
    const?: unknown;
    default?: unknown;
    format?: string;
    minLength?: number;
    minItems?: number;
    minimum?: number;
    maximum?: number;
    'x-derived'?: boolean;
    'x-widget'?: 'cgtModels' | 'aggregateSelector' | 'hierarchy' | 'upload' | string;
    'x-accept'?: string;
};

export type JobTypeProjection = {
    type: string;
    executor: JobExecutor;
    schedulable: boolean;
    argsSchema: JobArgsSchema;
    /** Runs of the type at the same time per company; null when unlimited. */
    maxConcurrent: number | null;
    /** What the caller may do with this type. */
    permissions: { launch: boolean; schedule: boolean };
};

export type ConcurrencyPolicy = 'forbid' | 'allow';

export type JobSchedule = {
    id: string;
    company: string | null;
    system: boolean;
    /** Set on the schedules the deployment owns: never editable from the UI. */
    systemKey: string | null;
    type: string;
    args: Record<string, unknown>;
    cron: string;
    timezone: string;
    enabled: boolean;
    nextRunAt: string | null;
    lastJobId: string | null;
    lastJobState: JobState | null;
    concurrencyPolicy: ConcurrencyPolicy;
    startingDeadlineSeconds: number;
    resources: JobResources | null;
    createdBy: { username: string | null } | null;
    createdAt: string | null;
    updatedAt: string | null;
};

/**
 * What the overview and the drill-down tables render: one attempt's result (the old `data`),
 * its file and its warning/error log lines, keyed by job type so each result narrows.
 */
export type GenericJobResult<JobType extends string, LogType, LogData> = {
    id: string;
    type: JobType;
    data?: LogData;
    logs: {
        infos: LogType[];
        warnings: LogType[];
        errors: LogType[];
    };
    filename?: string | null;
    /** Set when this attempt's file is the job attachment, served by the attachment endpoint. */
    attachmentJobId?: string;
};

export enum CatImportJobDataFields {
    CREATED_NODES = 'createdNodes',
    ITEMS_MODIFIED_PRICING = 'itemsWithPricingChanges',
    ITEMS_NON_RELEVANT_MODIFIED = 'itemsWithNonRelevantChanges',
    ITEMS_NOT_MODIFIED = 'itemsNotModified',
    ITEMS_DISABLED = 'itemsDisabled',
    UPDATED_GLS = 'updatedGls',
    ALIGNED_GLS = 'alignedGls',
    ALIGNED_MODELS = 'alignedModels',
}

/** Fields the CAT import writes on top of the shared import ones. */
export enum CatImportExtraJobDataFields {
    CREATED_FAMILIES = 'createdFamilies',
    CREATED_MODELS = 'createdModels',
    ITEMS_BLOCKED_PRICE_UPDATE = 'itemsWithBlockedPriceUpdate',
    ITEMS_BLOCKED_NOT_CHARGED_UPDATE = 'itemsWithBlockedNotChargedUpdate',
}

export enum AlignPricesJobDataFields {
    UPDATED_GLS = 'updatedGls',
    UPDATED_MODELS = 'updatedModels',
}

export enum RestartTranslationJobDataFields {
    ITEMS_MODIFIED = 'itemsModified',
}

export enum PublishFamilyJobDataFields {
    CREATED_NODES = 'createdNodes',
}

export enum CloseFamilyJobDataFields {
    REMOVED_SCHEDE = 'removedSchede',
}

export enum ReopenFamilyJobDataFields {
    REPUBLISHED_SCHEDE = 'republishedSchede',
}

export enum DuplicateNodeJobDataFields {
    CREATED_NODES = 'createdNodes',
}

export enum SapHierarchyJobDataFields {
    CREATED_NODES = 'createdNodes',
}

export enum ImportIdpUsersJobDataFields {
    CREATED_NODES = 'createdNodes',
    ITEMS_MODIFIED = 'itemsModified',
    ITEMS_DISABLED = 'itemsDisabled',
    ITEMS_NOT_MODIFIED = 'itemsNotModified',
}

export type JobDataItem = {
    hierarchyId: string;
    nodeId: string;
    code: string;
    changes: string[];
    isReplacement?: boolean;
    /**
     * CGT model of a prototype row. Prototypes are edited on their own page, addressed by model,
     * so the drill-down opens it there instead of the generic node editor.
     */
    prototypeModelId?: string;
};

export type DetailSummaryKey =
    | DetailType.PRICING_CHANGES
    | DetailType.NON_RELEVANT_CHANGES
    | DetailType.DISABLED
    | DetailType.CREATED
    | DetailType.CREATED_MODIFIED
    | DetailType.NON_MODIFIED
    | DetailType.ALIGNED_MODELS
    | DetailType.ALIGNED_GLS
    | DetailType.UPDATED_GLS
    | DetailType.UPDATED_MODELS
    | DetailType.NEW_FAMILIES_ADDED
    | DetailType.REALIGNED_FAMILIES
    | DetailType.CREATED_FAMILIES
    | DetailType.CREATED_MODELS
    | DetailType.REMOVED_SCHEDE
    | DetailType.REPUBLISHED_SCHEDE
    | DetailType.BLOCKED_PRICE_UPDATE
    | DetailType.BLOCKED_NOT_CHARGED_UPDATE
    | DetailType.FAILURES
    | DetailType.WARNINGS;

export type Detail = Omit<JobDataItem, 'changes'> & { change?: string | null };

export type DetailSummaryTypes = Record<
    DetailSummaryKey,
    {
        descriptionComponent: JSX.Element;
        details: string[] | Detail[];
    }
>;

type CatImportJobDialogData = Record<CatImportJobDataFields, JobDataItem[]> &
    Partial<Record<CatImportExtraJobDataFields, JobDataItem[]>>;
type ImportJobDialogBaseData = Record<CatImportJobDataFields, JobDataItem[]>;
export type ImportJobDialogCGTData = ImportJobDialogBaseData & {
    cgtRecap: Record<CatImportJobDataFields, JobDataItem[]>;
};
export type ImportJobDialogData = ImportJobDialogBaseData | ImportJobDialogCGTData;
type RestartTranslationJobData = Record<RestartTranslationJobDataFields, string[]>;
export type AlignPricesJobData = Record<AlignPricesJobDataFields, string[]>;
export type PublishFamilyJobData = Record<PublishFamilyJobDataFields, JobDataItem[]>;
export type CloseFamilyJobData = Record<CloseFamilyJobDataFields, JobDataItem[]>;
export type ReopenFamilyJobData = Record<ReopenFamilyJobDataFields, JobDataItem[]>;

export type DuplicateNodeJobData = Record<DuplicateNodeJobDataFields, JobDataItem[]>;

export type ImportIdpUsersJobData = Record<ImportIdpUsersJobDataFields, JobDataItem[]> & {
    hierarchyId: string;
    /** True when the job only reported the differences without writing them. */
    dryRun: boolean;
    idpUserCount: number;
    reEnabledUsernames: string[];
    skippedUsernames: string[];
    logIssues: string[];
};

export type SapHierarchyJobData = {
    createdNodes: { hierarchyId: string; nodeId: string; code: string }[];
    newFamiliesAdded: { code: string; description: string }[];
    /** Nodes whose family pointed at the wrong option and was rewritten onto the right one. */
    realignedFamilies?: {
        hierarchyId: string;
        nodeId: string;
        code: string;
        previous: string;
        current: string;
    }[];
};

export type CatJobType = GenericJobResult<
    'CatImport' | 'cat-import',
    string,
    CatImportJobDialogData
>;
type ImportJobType = GenericJobResult<'import', string, ImportJobDialogData>;
type PublishJobType = GenericJobResult<'Publish', string, unknown>;
type ExportXlsx = GenericJobResult<'ExportXlsx', string, unknown>;
type ExportHistory = GenericJobResult<'ExportHistory', string, unknown>;
type RestartTranslation = GenericJobResult<
    'restart-translation',
    string,
    RestartTranslationJobData
>;
type AlignPrices = GenericJobResult<'AlignPrices', string, AlignPricesJobData>;
type ZeusCoolsalesDiff = GenericJobResult<'zeus-coolsales-diff', string, unknown>;
type PublishFamily = GenericJobResult<'publish-family', string, PublishFamilyJobData>;
type CloseFamily = GenericJobResult<'close-family', string, CloseFamilyJobData>;
type ReopenFamily = GenericJobResult<'reopen-family', string, ReopenFamilyJobData>;
type DuplicateNode = GenericJobResult<'duplicate-node', string, DuplicateNodeJobData>;
type SapHierarchyImport = GenericJobResult<'cgt-import-sap-hierarchy', string, SapHierarchyJobData>;
/** The MM Dispo nodes the SAP alignment created, renamed, deleted or kept because still linked. */
export type SapMmDispoJobData = {
    aggregateId: string;
    /** The run only planned the alignment: the nodes listed were not written. */
    dryRun?: boolean;
    createdNodes: JobDataItem[];
    itemsModified: JobDataItem[];
    deletedNodes: Omit<JobDataItem, 'changes'>[];
    skippedLinkedNodes: (Omit<JobDataItem, 'changes'> & { linkedBy: string[] })[];
};
type SapMmDispoImport = GenericJobResult<'cgt-import-sap-mm-dispo', string, SapMmDispoJobData>;
/** The monthly ricambi import: unchanged parts are only counted, like the CAT import. */
export type RicambiImportJobData = {
    aggregateId: string;
    fileName: string | null;
    rows: number;
    createdNodes: JobDataItem[];
    itemsModified: JobDataItem[];
    itemsNotModifiedCount: number;
};
type RicambiImport = GenericJobResult<'cgt-import-ricambi', string, RicambiImportJobData>;
type ImportIdpUsers = GenericJobResult<'import-idp-users', string, ImportIdpUsersJobData>;
/** The nodes a find & replace apply wrote, and the ones it left (conflicts, skipped values). */
export type FindReplaceApplyJobData = {
    aggregateId: string;
    itemsModified: JobDataItem[];
    itemsNotModified: JobDataItem[];
};
type FindReplaceApply = GenericJobResult<'find-replace-apply', string, FindReplaceApplyJobData>;
/** A rule run reports its nodes like the apply only when it applied on its own. */
type BulkChangeRuleRun = GenericJobResult<
    'bulk-change-rule',
    string,
    Partial<FindReplaceApplyJobData>
>;

export type JobResultView =
    | CatJobType
    | ImportJobType
    | PublishJobType
    | ExportXlsx
    | RestartTranslation
    | ExportHistory
    | AlignPrices
    | ZeusCoolsalesDiff
    | PublishFamily
    | CloseFamily
    | ReopenFamily
    | DuplicateNode
    | SapHierarchyImport
    | SapMmDispoImport
    | RicambiImport
    | ImportIdpUsers
    | FindReplaceApply
    | BulkChangeRuleRun;

// The values double as the drill-down key and as the BaseTable column id prefix
// (_.camelCase(detailType) in JobDetails), so they must stay stable. Display text comes from
// DETAIL_TYPE_LABELS instead.
export enum DetailType {
    OVERVIEW = 'overview',
    CREATED = 'Created',
    CREATED_MODIFIED = 'Created and Modified',
    PRICING_CHANGES = 'Pricing Changes',
    NON_RELEVANT_CHANGES = 'Non Relevant Changes',
    NON_MODIFIED = 'Non modified',
    DISABLED = 'Disabled',
    ALIGNED_MODELS = 'Aligned Models',
    ALIGNED_GLS = 'Aligned Gls',
    UPDATED_GLS = 'Updated Gls',
    UPDATED_MODELS = 'Updated Models',
    NEW_FAMILIES_ADDED = 'New Families Added',
    REALIGNED_FAMILIES = 'Realigned Families',
    CREATED_FAMILIES = 'Created Families',
    CREATED_MODELS = 'Created Models',
    REMOVED_SCHEDE = 'Removed Schede',
    REPUBLISHED_SCHEDE = 'Republished Schede',
    BLOCKED_PRICE_UPDATE = 'Blocked Price Update',
    BLOCKED_NOT_CHARGED_UPDATE = 'Blocked Not Charged Update',
    FAILURES = 'Failures',
    WARNINGS = 'Warnings',
}

export enum CounterType {
    SUCCESS = 'success',
    ERROR = 'error',
    WARNING = 'warning',
    UPDATED = 'updated',
    INFO = 'info',
}
